import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { FarcasterProvider } from './contexts/FarcasterContext';
import { autoCleanAuthStorage } from './utils/authCleanup';

// Components
import Navbar from './components/layout/Navbar';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ErrorBoundary from './components/ui/ErrorBoundary';
import FarcasterSplashScreen from './components/ui/FarcasterSplashScreen';

// Pages (using lazy loading for better performance)
// const HomePage = React.lazy(() => import('./pages/HomePage')); // Hidden for now
const ExplorePage = React.lazy(() => import('./pages/ExplorePage'));
const CollectionPage = React.lazy(() => import('./pages/CollectionPage'));
const NFTDetailPage = React.lazy(() => import('./pages/NFTDetailPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const SharePage = React.lazy(() => import('./pages/SharePage'));
const NotificationTestPage = React.lazy(() => import('./pages/NotificationTestPage'));
// const CreatePage = React.lazy(() => import('./pages/CreatePage')); // Hidden for now
// const FarcasterFeedPage = React.lazy(() => import('./pages/FarcasterFeedPage')); // Hidden for now
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

/**
 * CRITICAL: Main App Component
 * 
 * This is the root component that:
 * - Sets up routing for all pages
 * - Provides consistent layout (navbar, footer)
 * - Handles global error boundaries
 * - Manages loading states
 * - Integrates toast notifications
 * 
 * DO NOT remove ErrorBoundary - it prevents the entire app from crashing.
 * All routes must be wrapped in Suspense for lazy loading to work.
 */

function App() {
  // Auto-clean corrupted authentication data on app start
  useEffect(() => {
    autoCleanAuthStorage();
  }, []);

  return (
    <FarcasterSplashScreen
      disableNativeGestures={false} // Enable gestures for NFT interactions
      onReady={() => console.log('🎉 Castoria Fortress is ready!')}
    >
      <FarcasterProvider>
        <div className="min-h-screen bg-secondary-900 text-white flex flex-col">
          {/* Global error boundary to catch all unhandled errors */}
          <ErrorBoundary>
            {/* Navigation header */}
            <Navbar />
            
            {/* Main content area */}
            <main className="flex-1">
              <Suspense 
                fallback={
                  <div className="flex items-center justify-center min-h-[60vh]">
                    <LoadingSpinner size="lg" />
                  </div>
                }
              >
                <Routes>
                  {/* Redirect root to explore */}
                  <Route path="/" element={<Navigate to="/explore" replace />} />
                  
                  {/* Explore page - main NFT marketplace */}
                  <Route path="/explore" element={<ExplorePage />} />
                  
                  {/* Collection pages */}
                  <Route path="/collection/:contractAddress" element={<CollectionPage />} />
                  
                  {/* Individual NFT detail page */}
                  <Route 
                    path="/nft/:contractAddress/:tokenId" 
                    element={<NFTDetailPage />} 
                  />
                  
                  {/* User profile pages */}
                  <Route path="/profile/:address" element={<ProfilePage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  
                  {/* Share extension page for receiving shared casts */}
                  <Route path="/share" element={<SharePage />} />
                  
                  {/* Notification system test page */}
                  <Route path="/test-notifications" element={<NotificationTestPage />} />
                  
                  {/* Hidden pages - uncomment to re-enable */}
                  {/* <Route path="/home" element={<HomePage />} /> */}
                  {/* <Route path="/create" element={<CreatePage />} /> */}
                  {/* <Route path="/feed" element={<FarcasterFeedPage />} /> */}
                  
                  {/* 404 page for unmatched routes */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </main>
            
            {/* Global toast notifications */}
            <Toaster
              position="bottom-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#1f2937', // Updated secondary-800
                  color: '#F0F0F0', // Castoria light gray
                  border: '1px solid #4B0082', // Castoria deep purple
                  borderRadius: '8px',
                },
                success: {
                  iconTheme: {
                    primary: '#FFD700', // Castoria gold
                    secondary: '#191970', // Midnight blue
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444', // Keep error red
                    secondary: '#F0F0F0',
                  },
                },
              }}
            />
          </ErrorBoundary>
        </div>
      </FarcasterProvider>
    </FarcasterSplashScreen>
  );
}

export default App; 